import {
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { GridListItem, GridListItemProps } from 'components'

import { useTrackingModalStore } from 'modules/orders/components/Tracking/trackingModalStore'
import Tracking from 'modules/orders/components/Tracking/Tracking'
import { useFormContext } from 'react-hook-form'
import { useOrderStore } from 'modules/orders/store'
import { LastSalesModal } from 'modules/orders/components/LastSalesModal'
import { get } from 'lodash'

import { ItemCommission } from './itemCommission'
import {
  useLoadProductWeight,
  useLoadProductStockByMaterial
} from 'modules/orders/hooks'
import { formatCurrency, formatNumber, stringToNumber } from 'utils'
import { ItemAttachments } from './ItemAttachments'
import { useMemo } from 'react'

const detailsGeneral: GridListItemProps[] = [
  {
    label: 'ICMS',
    key: 'icmsPercent',
    transform: (value: any) => `${value}%`
  },
  {
    label: 'IPI',
    key: 'ipiPercent',
    transform: (value: any) => `${value}%`
  }
  // {
  //   label: 'ST',
  //   key: 'stPercent',
  //   transform: (value: any) => `${value}%`
  // }

  // {
  //   label: 'Frete',
  //   key: 'freightPercent',
  //   transform: (value: any) => `${value}%`
  // }
]

const weights: any = [
  // {
  //   label: 'Líquido',
  //   key: 'product.netWeight',
  //   transform: (value: any, item: any) =>
  //     `${value}KG | ${value * +item.quantity}KG`
  // },
  {
    label: 'Bruto',
    key: 'grossWeight',
    transform: (value: any, item: any) =>
      `${value.toFixed(2)}KG | ${(
        value * Number(stringToNumber(item.quantity))
      ).toFixed(2)}KG`
  },
  {
    label: 'Valor por KG',
    key: 'hasWeightPrice',
    transform: (value: any, item: any) =>
      value
        ? formatCurrency(+item.salePrice / +item.grossWeight)
        : 'não definido'
  }
]

export const ItemDetails = (props: any) => {
  const { item, sequence } = props
  const isTrackingOpen = useTrackingModalStore.useIsModalOpen()

  const form = useOrderStore.useForm()
  const { watch } = useFormContext()

  const customerId = watch('customer')?.id
  const unityId = watch('unity')?.id

  const productIdForStock = item?.product?.id || item?.id
  const unitMeasureForStock =
    item?.product?.unitMeasure || item?.unitMeasure || ''

  const { data: grossWeight } = useLoadProductWeight({
    productId: item?.product?.id,
    identifyId: item?.configurationId
  })

  const especif1FromConfigurator = item?.configurationId || ''

  const { data: stockByMaterialResponse } = useLoadProductStockByMaterial(
    productIdForStock,
    { unityId, especif1: especif1FromConfigurator },
    !!productIdForStock && !!unityId && !!especif1FromConfigurator
  )

  const stockByEspec1 = useMemo(() => {
    const stockByMaterial = stockByMaterialResponse?.data || []
    const groups: { especif1: string; items: any[] }[] = []
    const map = new Map<string, any[]>()

    stockByMaterial.forEach((row: any) => {
      const key = row.especif1 || 'Sem especificação'
      if (!map.has(key)) {
        map.set(key, [])
      }
      map.get(key)?.push(row)
    })

    map.forEach((items, especif1) => {
      groups.push({ especif1, items })
    })

    return groups
  }, [stockByMaterialResponse?.data])

  const hasStockRows = stockByEspec1.length > 0

  const itemAttachData = useMemo(
    () => ({
      customerId,
      orderId: form.orderId,
      subject: `Web Ped: ${form.orderId} Mat.: ${item.product.id} Conf.: ${item.configurationId} Seq.: ${sequence}`,
      type: 'I',
      title: {
        id: '502',
        name: 'ACOMPANHAMENTO DO ITEM'
      },
      item: {
        id: item.product.id,
        configurationId: item.configurationId,
        sequence
      }
    }),
    [customerId, form.orderId, item.configurationId, item.product.id, sequence]
  )

  return (
    <>
      {isTrackingOpen && <Tracking />}

      <Grid container style={{ marginBottom: 60 }}>
        <Grid item xs={12} style={{ marginTop: 8 }}>
          <Divider />
        </Grid>
        <Grid
          container
          item
          rowSpacing={1}
          style={{
            marginTop: 5,
            paddingRight: 16
          }}
        >
          <Grid item container xs={12} spacing={2}>
            <Grid item xs={6}>
              <LastSalesModal productId={item?.product?.id} />
            </Grid>
            <Grid item xs={6}>
              <ItemAttachments data={itemAttachData} />
            </Grid>
          </Grid>

          {!!productIdForStock && !!unityId && !!especif1FromConfigurator && (
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Estoque
              </Typography>
              <Table size="small" sx={{ '& td, & th': { py: 0.5, px: 1 } }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell variant="head">Identificador</TableCell>
                    <TableCell variant="head">Centro</TableCell>
                    <TableCell variant="head" align="right">
                      Qtd
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasStockRows ? (
                    stockByEspec1.flatMap((group: any) =>
                      (group.items || []).map((row: any, index: number) => (
                        <TableRow key={`${group.especif1}-${index}`}>
                          <TableCell>{group.especif1}</TableCell>
                          <TableCell>
                            {row.centroArmazenagem || row.centro_armazenagem}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.quantidade || 0)}{' '}
                            {unitMeasureForStock}
                          </TableCell>
                        </TableRow>
                      ))
                    )
                  ) : (
                    <TableRow>
                      <TableCell>{especif1FromConfigurator || '-'}</TableCell>
                      <TableCell>-</TableCell>
                      <TableCell align="right">Sem Estoque</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Grid>
          )}

          <Grid item xs={12}>
            <ItemCommission item={item} />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Peso</Typography>
          </Grid>
          <Grid item xs={12} container>
            {weights.map((detail: any) => (
              <GridListItem
                key={detail.key}
                label={detail?.label}
                value={get(
                  {
                    ...item,
                    grossWeight: grossWeight?.data?.weight || 0,
                    hasWeightPrice: grossWeight?.data?.hasWeightPrice || 0
                  },
                  detail.key
                )}
                transform={(value) =>
                  detail?.transform(value, {
                    ...item,
                    grossWeight: grossWeight?.data?.weight || 0,
                    hasWeightPrice: grossWeight?.data?.hasWeightPrice
                  })
                }
              />
            ))}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Impostos</Typography>
          </Grid>
          <Grid item xs={12} container>
            {detailsGeneral.map((detail: any) => (
              <GridListItem
                key={detail.key}
                label={detail?.label}
                value={get({ ...item }, detail.key)}
                transform={detail?.transform}
              />
            ))}
          </Grid>
          {/* <Grid item xs={12}>
    <Typography variant="h6">
      Informações de Tabela de Preço
    </Typography>
  </Grid>
  <Grid item xs={12} container>
    {detailsPriceTable.map((detail: any) => (
      <GridListItem
        key={detail.key}
        label={detail?.label}
        value={get(
          {
            ...item,
            ...priceTable,
            commissionPercent: productResp?.data?.commissionPercent
          },
          detail.key
        )}
        transform={detail?.transform}
      />
    ))}
  </Grid> */}

          {/* <Grid item xs={4}>
  <Typography variant="caption">Desconto Máximo</Typography>
  <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={1}>
    {formatCurrency(maxDiscount[1])} | {maxDiscount[0].toFixed(2)}%
  </Typography>
</Grid>
<Grid item xs={4}>
  <Typography variant="caption">Valor Mínimo</Typography>
  <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={1}>
    {formatCurrency(item?.minorPrice)}
  </Typography>
</Grid>
<Grid item xs={4}>
  <Typography variant="caption">Valor Máximo</Typography>
  <Typography style={{ fontSize: 14, fontWeight: 500 }} mb={1}>
    {formatCurrency(item.majorPrice)}
  </Typography>
</Grid> */}

          {/* <Grid item xs={12}>
            <Typography variant="h6">Estoque</Typography>
          </Grid>

          <Grid item xs={12} style={{ paddingBottom: 16 }}>
            <StockTable rows={item?.stock || []} />
          </Grid> */}
        </Grid>
      </Grid>
    </>
  )
}
